import { useRef, useState } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';

// project imports
import { ThemeMode } from 'config';

import Transitions from 'ui-component/extended/Transitions';
// assets
import FlagUS from '../../../../assets//images/flags/US.png';
import FlagFR from '../../../../assets/images/flags/French.png';
import FlagDE from '../../../../assets/images/flags/German.png';
import FlagJA from '../../../../assets/images/flags/Japanese.png';
import FlagCN from '../../../../assets/images/flags/Chinese.png';
import FlagTW from '../../../../assets/images/flags/Taiwan.png';
import useConfig from 'hooks/useConfig';

// ==============================|| LOCALIZATION ||============================== //

export default function LocalizationSection() {
  const { borderRadius, i18n, onChangeLocale } = useConfig();
  const theme = useTheme();
  const downMD = useMediaQuery(theme.breakpoints.down('md'));

  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  // Centralized Language List
  const LANGUAGES = [
    { code: 'en', label: 'English', icon: FlagUS },
    { code: 'fr', label: 'French', icon: FlagFR },
    { code: 'de', label: 'German', icon: FlagDE },
    { code: 'ja', label: 'Japanese', icon: FlagJA },
    { code: 'zh', label: 'Chinese (Simplified)', icon: FlagCN },
    { code: 'tw', label: 'Chinese (Traditional)', icon: FlagTW }
  ];

  // handlers
  const handleToggle = () => setOpen((prev) => !prev);
  const handleClose = (event) => {
    if (anchorRef.current?.contains(event.target)) return;
    setOpen(false);
  };
  const handleListItemClick = (_, lng) => {
    onChangeLocale(lng);
    setOpen(false);
  };

  return (
    <>
      {/* Trigger Button */}
      <Box sx={{ position: 'relative', ml: { md: 0.5 }, cursor: 'pointer' }}>
        <IconButton ref={anchorRef} onClick={handleToggle}>
          {/*  Selected Language Label */}
          <Box component="img" src={LANGUAGES.find((x) => x.code === i18n)?.icon} width={24} sx={{ borderRadius: '2px' }} />
        </IconButton>
      </Box>

      {/* Dropdown */}
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement={downMD ? 'bottom' : 'bottom'}
        transition
        disablePortal
        modifiers={[
          {
            name: 'offset',
            options: { offset: [0, 14] }
          }
        ]}
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={handleClose}>
            <Transitions in={open} {...TransitionProps}>
              <Paper
                sx={{
                  borderRadius: `${borderRadius}px`,
                  border: '1px solid',
                  borderColor: theme.palette.mode === ThemeMode.DARK ? theme.palette.divider : 'transparent',
                  boxShadow: '0 3px 14px 2px rgba(0,0,0,0.12), 0 8px 10px 1px rgba(0,0,0,0.14), 0 5px 5px -3px rgba(0,0,0,0.20)'
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: -5,
                    left: '50%',
                    width: 15,
                    height: 15,
                    backgroundColor: 'background.paper',
                    transform: 'rotate(45deg)',
                    zIndex: -1,
                    borderTop: '1px solid',
                    borderLeft: '1px solid',
                    borderColor: 'divider',
                    marginLeft: '-8px'
                  }}
                />
                <List sx={{ minWidth: 220 }}>
                  {LANGUAGES.map((lang) => (
                    <ListItemButton
                      key={lang.code}
                      selected={i18n === lang.code}
                      onClick={(e) => handleListItemClick(e, lang.code)}
                      sx={{
                        py: 0.7,
                        '&:hover': {
                          backgroundColor: theme.palette.mode === ThemeMode.DARK ? theme.palette.dark[700] : theme.palette.grey[200]
                        }
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box component="img" src={lang.icon} width={24} sx={{ mr: 1.5 }} />
                            <Typography variant="body1" color="grey.800">
                              {lang.label}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Paper>
            </Transitions>
          </ClickAwayListener>
        )}
      </Popper>
    </>
  );
}
